import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, Building2, User, CheckCircle2, AlertTriangle, Shield, Wifi, WifiOff, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function PatientCard({ p }) {
  return (
    <Card className="glass-card border-border" data-testid="nhs-patient-card">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#00A6A0]" />
            <span className="text-sm font-medium text-foreground">
              {(p.prefix || []).join(' ')} {(p.given_names || []).join(' ')} {p.family_name}
            </span>
            {p.deceased && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">Deceased</span>}
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#005EB8]/10 text-[#005EB8] font-medium" data-testid="source-badge">
            {p.source || 'NHS_PDS'}
          </span>
        </div>
        {p.retrieved_at && (
          <p className="text-[10px] text-muted-foreground">Retrieved: {new Date(p.retrieved_at).toLocaleString()} - verify before acting on this information</p>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-muted-foreground">NHS Number:</span> <span className="text-foreground font-mono">{p.nhs_number}</span></div>
          <div><span className="text-muted-foreground">DOB:</span> <span className="text-foreground">{p.birth_date}</span></div>
          <div><span className="text-muted-foreground">Gender:</span> <span className="text-foreground capitalize">{p.gender}</span></div>
          {p.address && <div className="col-span-2"><span className="text-muted-foreground">Address:</span> <span className="text-foreground">{p.address}</span></div>}
        </div>
        {p.gp_practice?.ods_code && (
          <div className="flex items-center gap-2 text-xs bg-muted p-2 rounded-lg">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">GP:</span>
            <span className="text-foreground">{p.gp_practice.display || p.gp_practice.ods_code}</span>
            <span className="text-muted-foreground font-mono">({p.gp_practice.ods_code})</span>
          </div>
        )}
        {p.telecom?.length > 0 && (
          <div className="flex gap-3 text-xs">
            {p.telecom.map((t, i) => (
              <span key={i} className="flex items-center gap-1 text-muted-foreground">
                <Phone className="w-3 h-3" />{t.value}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function NHSConnect() {
  const { user } = useAuth();
  const [tab, setTab] = useState('patient');
  const [nhsNumber, setNhsNumber] = useState('');
  const [searchParams, setSearchParams] = useState({ family: '', given: '', birthdate: '', gender: '' });
  const [odsCode, setOdsCode] = useState('');
  const [patientResult, setPatientResult] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [orgResult, setOrgResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessRecord, setAccessRecord] = useState(null);
  const [accessNhs, setAccessNhs] = useState('');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { if (isAdmin) fetchStatus(); }, []);

  const fetchStatus = async () => {
    try { const { data } = await axios.get(`${API}/api/nhs/status`, { withCredentials: true }); setStatus(data); }
    catch { /* ignore */ }
  };

  const lookupPatient = async () => {
    if (!nhsNumber.trim()) return toast.error('Enter an NHS number');
    setLoading(true); setPatientResult(null);
    try {
      const { data } = await axios.get(`${API}/api/nhs/pds/patient/${nhsNumber.replace(/\s/g, '')}`, { withCredentials: true });
      setPatientResult(data);
      if (data.status === 'not_found') toast.info('No patient found with that NHS number');
      else if (data.status === 'error') toast.error(data.message || 'PDS lookup failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  const searchPatients = async () => {
    if (!searchParams.family && !searchParams.given) return toast.error('Enter at least a family name');
    setLoading(true); setSearchResults(null);
    try {
      const params = Object.fromEntries(Object.entries(searchParams).filter(([,v]) => v));
      const { data } = await axios.get(`${API}/api/nhs/pds/search`, { params, withCredentials: true });
      setSearchResults(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Search failed'); }
    finally { setLoading(false); }
  };

  const lookupPractice = async () => {
    if (!odsCode.trim()) return toast.error('Enter an ODS code');
    setLoading(true); setOrgResult(null);
    try {
      const { data } = await axios.get(`${API}/api/nhs/gp-connect/organization/${odsCode.trim().toUpperCase()}`, { withCredentials: true });
      setOrgResult(data);
      if (data.status === 'error') toast.error(data.message || 'Lookup failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Lookup failed'); }
    finally { setLoading(false); }
  };

  const fetchAccessRecord = async () => {
    if (!accessNhs.trim()) return toast.error('Enter an NHS number');
    setLoading(true); setAccessRecord(null);
    try {
      const { data } = await axios.get(`${API}/api/nhs/gp-connect/access-record/${accessNhs.replace(/\s/g, '')}`, { withCredentials: true });
      setAccessRecord(data);
      if (data.status === 'error') toast.error(data.message || 'Failed to retrieve GP record');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="nhs-connect-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#005EB8]" />NHS Connect
            </h1>
            <p className="text-sm text-muted-foreground mt-1">PDS Patient Demographics + GP Practice Lookup</p>
          </div>
          {status && (
            <div className="flex items-center gap-2 text-xs">
              {status.sandbox_available ? (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#A8E6CF]/10 text-[#A8E6CF]"><Wifi className="w-3 h-3" />Sandbox Connected</span>
              ) : (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-400"><WifiOff className="w-3 h-3" />Sandbox Offline</span>
              )}
              <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground">{status.environment?.toUpperCase()}</span>
            </div>
          )}
        </div>

        {status?.environment === 'sandbox' && (
          <div className="space-y-2">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs" data-testid="pre-assurance-banner">
              <strong className="text-amber-400">PRE-ASSURANCE MODE</strong>
              <span className="text-muted-foreground"> — This system has not completed NHS England technical conformance (DCB0129), clinical safety assessment, or consumer system onboarding. GP Connect data is from the NHS sandbox and must not be used for clinical decisions.</span>
            </div>
            <div className="bg-[#005EB8]/10 border border-[#005EB8]/20 rounded-lg p-3 text-xs text-muted-foreground" data-testid="sandbox-banner">
              <strong className="text-[#005EB8]">NHS Sandbox</strong> — Returns synthetic test data only. Purpose: <strong>Direct Patient Care</strong> (SNOMED 386053000). GP Connect API v{status.gpc_api_version || '1.6.0'} / FHIR {status.fhir_version || 'STU3'}.
            </div>
          </div>
        )}

        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <button onClick={() => setTab('patient')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'patient' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-patient">Patient Lookup</button>
          <button onClick={() => setTab('search')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'search' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-search">Patient Search</button>
          <button onClick={() => setTab('practice')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'practice' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-practice">GP Practice</button>
          <button onClick={() => setTab('record')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'record' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-record">GP Record</button>
        </div>

        {/* NHS Number Lookup */}
        {tab === 'patient' && (
          <div className="space-y-4" data-testid="patient-lookup-section">
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-3">Look up a patient by their 10-digit NHS number (Modulus 11 validated)</p>
                <div className="flex gap-2">
                  <Input value={nhsNumber} onChange={e => setNhsNumber(e.target.value)} placeholder="e.g. 943 476 5919"
                    className="bg-muted border-border text-foreground flex-1 font-mono" data-testid="nhs-number-input"
                    onKeyDown={e => e.key === 'Enter' && lookupPatient()} />
                  <Button onClick={lookupPatient} disabled={loading} className="bg-[#005EB8] hover:bg-[#004C99] text-white" data-testid="lookup-btn">
                    <Search className="w-4 h-4 mr-1" />{loading ? 'Looking up...' : 'Lookup'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            {patientResult?.status === 'found' && patientResult.patient && <PatientCard p={patientResult.patient} />}
            {patientResult?.status === 'not_found' && (
              <div className="text-center py-8"><AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-2" /><p className="text-muted-foreground text-sm">No patient found</p></div>
            )}
          </div>
        )}

        {/* Demographics Search */}
        {tab === 'search' && (
          <div className="space-y-4" data-testid="patient-search-section">
            <Card className="glass-card border-border">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm text-muted-foreground">Search by patient demographics</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={searchParams.family} onChange={e => setSearchParams(s => ({ ...s, family: e.target.value }))} placeholder="Family name (surname)" className="bg-muted border-border text-foreground" data-testid="search-family" />
                  <Input value={searchParams.given} onChange={e => setSearchParams(s => ({ ...s, given: e.target.value }))} placeholder="Given name (first)" className="bg-muted border-border text-foreground" data-testid="search-given" />
                  <Input type="date" value={searchParams.birthdate} onChange={e => setSearchParams(s => ({ ...s, birthdate: e.target.value }))} className="bg-muted border-border text-foreground" data-testid="search-dob" />
                  <select value={searchParams.gender} onChange={e => setSearchParams(s => ({ ...s, gender: e.target.value }))} className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="search-gender">
                    <option value="">Any gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </div>
                <Button onClick={searchPatients} disabled={loading} className="bg-[#005EB8] hover:bg-[#004C99] text-white w-full" data-testid="search-btn">
                  <Search className="w-4 h-4 mr-1" />{loading ? 'Searching...' : 'Search PDS'}
                </Button>
              </CardContent>
            </Card>
            {searchResults?.status === 'found' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">{searchResults.total} result(s)</p>
                {searchResults.patients?.map((p, i) => <PatientCard key={i} p={p} />)}
              </div>
            )}
          </div>
        )}

        {/* GP Practice Lookup */}
        {tab === 'practice' && (
          <div className="space-y-4" data-testid="practice-lookup-section">
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-3">Look up a GP practice by ODS code</p>
                <div className="flex gap-2">
                  <Input value={odsCode} onChange={e => setOdsCode(e.target.value)} placeholder="e.g. A20047"
                    className="bg-muted border-border text-foreground flex-1 font-mono uppercase" data-testid="ods-code-input"
                    onKeyDown={e => e.key === 'Enter' && lookupPractice()} />
                  <Button onClick={lookupPractice} disabled={loading} className="bg-[#005EB8] hover:bg-[#004C99] text-white" data-testid="ods-lookup-btn">
                    <Building2 className="w-4 h-4 mr-1" />{loading ? 'Looking up...' : 'Lookup'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            {orgResult?.status === 'found' && orgResult.organizations?.map((o, i) => (
              <Card key={i} className="glass-card border-border" data-testid="gp-practice-card">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#005EB8]" />
                    <span className="text-sm font-medium text-foreground">{o.name}</span>
                    <span className="text-xs font-mono text-muted-foreground">({o.ods_code})</span>
                  </div>
                  {o.address && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{o.address}</p>}
                  {o.telecom?.length > 0 && (
                    <div className="flex gap-3 text-xs">{o.telecom.map((t, j) => <span key={j} className="text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{t.value}</span>)}</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* GP Record (Access Record HTML) */}
        {tab === 'record' && (
          <div className="space-y-4" data-testid="gp-record-section">
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-3">View a patient{"'"}s GP record via GP Connect Access Record (sandbox: synthetic data)</p>
                <div className="flex gap-2">
                  <Input value={accessNhs} onChange={e => setAccessNhs(e.target.value)} placeholder="NHS number e.g. 9000000009"
                    className="bg-muted border-border text-foreground flex-1 font-mono" data-testid="access-record-input"
                    onKeyDown={e => e.key === 'Enter' && fetchAccessRecord()} />
                  <Button onClick={fetchAccessRecord} disabled={loading} className="bg-[#005EB8] hover:bg-[#004C99] text-white" data-testid="access-record-btn">
                    {loading ? 'Loading...' : 'View GP Record'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {accessRecord?.status === 'found' && (
              <div className="space-y-3" data-testid="gp-record-view">
                {/* Source banner */}
                <div className="bg-[#005EB8]/10 border border-[#005EB8]/20 rounded-lg p-3">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-[#005EB8]/20 text-[#005EB8] font-bold text-[10px]">GP CONNECT RECORD</span>
                      <span className="text-foreground font-medium">{accessRecord.patient_name}</span>
                      <span className="text-muted-foreground font-mono">NHS: {accessRecord.patient_nhs_number}</span>
                    </div>
                    <div className="text-right text-[10px] text-muted-foreground">
                      <div>Retrieved: {new Date(accessRecord.retrieved_at).toLocaleString()}</div>
                      <div>Source: {accessRecord.metadata?.originating_organisation || 'Unknown'} ({accessRecord.metadata?.originating_ods_code})</div>
                    </div>
                  </div>
                  {accessRecord.assurance_notice && (
                    <p className="text-[10px] text-amber-400 mt-1">{accessRecord.assurance_notice}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">{accessRecord.metadata?.clinical_note}</p>
                </div>

                {/* Allergies */}
                <Card className="border-red-500/20">
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-red-400 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Allergies ({accessRecord.sections?.allergies?.length || 0})</p>
                    {(accessRecord.sections?.allergies || []).map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-red-500/5 p-2 rounded mb-1">
                        <span className="font-medium text-foreground">{a.substance}</span>
                        <span className="text-muted-foreground">{a.reaction}</span>
                        <span className="text-red-400 text-[10px]">{a.severity}</span>
                        <span className="text-muted-foreground text-[10px] ml-auto">{a.recorded}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Medications */}
                <Card className="glass-card border-border">
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-[#005EB8] mb-2 flex items-center gap-1"><Shield className="w-3 h-3" />Medications ({accessRecord.sections?.medications?.length || 0}) <span className="text-amber-400 text-[10px] ml-2">Verify discrepancies with prescriber</span></p>
                    {(accessRecord.sections?.medications || []).map((m, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-muted/30 p-2 rounded mb-1">
                        <div><span className="font-medium text-foreground">{m.name}</span> <span className="text-muted-foreground">{m.dose} {m.route} {m.frequency}</span></div>
                        <span className="text-[10px] text-muted-foreground">{m.prescriber} | {m.start}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Conditions */}
                <Card className="glass-card border-border">
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-foreground mb-2">Conditions ({accessRecord.sections?.conditions?.length || 0})</p>
                    {(accessRecord.sections?.conditions || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-muted/30 p-2 rounded mb-1">
                        <span className="text-foreground">{c.condition}</span>
                        <span className="text-muted-foreground text-[10px]">{c.status} | onset: {c.onset}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Consultations */}
                <Card className="glass-card border-border">
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-foreground mb-2">Recent Consultations ({accessRecord.sections?.consultations?.length || 0})</p>
                    {(accessRecord.sections?.consultations || []).map((c, i) => (
                      <div key={i} className="text-xs bg-muted/30 p-2 rounded mb-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-foreground font-medium">{c.date}</span>
                          <span className="text-muted-foreground">{c.type} - {c.clinician}</span>
                        </div>
                        <p className="text-muted-foreground">{c.notes}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {accessRecord?.status === 'timeout' && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-foreground">GP system unavailable</p>
                <p className="text-xs text-muted-foreground mt-1">The practice system may be offline. Contact the practice directly for urgent information.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
